import { ActionTree } from "vuex";
import {
	DeviceChange,
	DeviceChanges,
	DeviceChangesResponse,
	DeviceConfiguration,
	DeviceConfigurationAlarm,
	DeviceConfigurationAudio,
	DeviceConfigurationCamera,
	DeviceConfigurationRelay,
	DeviceConfigurationSelections,
	DeviceDetails as IDeviceDetails,
	DeviceRecordsRetrievalStatus,
	DevicesState,
	DeviceType,
	DeviceTypeCounts,
	DeviceTypeIdentifier,
	ServerDetails,
	DeviceSearchQuery,
	DeviceSearchSwipeAndShowQuery,
	FetchServerByGroupParams,
	SearchServerByGroupParams,
	GetDeviceForGroupByTypeParams,
	GetDeviceParams,
	GetDevicesByTypeParams,
	ServerConfiguration,
	ServerType,
	ServersWithEnabledDeviceTypesParams,
	AddDevicesForServerParams,
	FetchConfigForServerParams,
	ServerConfigurationResponse,
	UpdateType,
	DeviceConfigurationPreset,
	DeviceConfigurationPresetPayload
} from "@/store/devices/types";
import { CancellableQuery, PaginatedQueryParams, PaginatedSearchQueryParams, RootState } from "@/store/types";
import api from "@/services/api.service";
import { devicesApi } from "@/services/api.devices.service";
import axios from "axios";
import { DeviceDetails } from "@sureview/v2-cameras";
import { ICamera } from "../views/types";
import QuickControlResponse from "@/types/QuickControlResponse";
import objectToCamelCase from "@/utils/objectToCamelCase";

export const actions: ActionTree<DevicesState, RootState> = {
	async deviceAction(context, { deviceID, payload }) {
		const { data } = await api.deviceAction(deviceID, payload);
		return data;
	},

	async deviceSearch({ commit, rootGetters }, query: string | { search: string; regionUrl: string }): Promise<void> {
		const cancelTokenSource = axios.CancelToken.source();
		commit("setCancelTokenSource", cancelTokenSource);

		const { data } =
			typeof query == "string"
				? await api.deviceSearch(3, query, cancelTokenSource.token)
				: await api.deviceSearch(3, query.search, cancelTokenSource.token, false, query.regionUrl);

		commit("setDoors", data);
	},

	async deviceSearchSwipeAndShow( { commit }, { query, searchTermsInAnyOrder, regionUrl }: DeviceSearchSwipeAndShowQuery ): Promise<void> {
		const cancelTokenSource = axios.CancelToken.source();
		commit("setCancelTokenSource", cancelTokenSource);
		const { data } = await api.deviceSearch(3, query, cancelTokenSource.token, searchTermsInAnyOrder, regionUrl);
		commit("setDoors", data);
	},

	async deviceSearchAndAppendToGroupTree( { commit }, { query, deviceTypeId, searchTermsInAnyOrder = false }: DeviceSearchQuery) {
		const cancelTokenSource = axios.CancelToken.source();
		commit("setCancelTokenSource", cancelTokenSource);

		const { data } = await api.deviceSearch(deviceTypeId, query, cancelTokenSource.token, searchTermsInAnyOrder);

		// remapping object as the property casing does not match what the camer plugin and other code is expecting
		let devices = data.map(device => {
			return {
				deviceID: device.deviceId,
				deviceTypeID: device.deviceTypeId,
				title: device.title,
				groupId: device.groupId,
				settingsMeta: device.settingsMeta
			} as ICamera;
		});

		commit("eventqueue/setDevicesAndGroupsInSiteTree", { devices, query, searchTermsInAnyOrder }, { root: true });
	},

	async deviceQuickControl(
		context,
		{
			deviceId,
			reason,
			eventId,
			regionUrl
		}: { deviceId: number; reason: string; eventId: number; regionUrl?: string }
	): Promise<QuickControlResponse> {
		const { data } = await api.deviceQuickControl(deviceId, reason, eventId, regionUrl);
		return data;
	},

	async getServers({ commit }): Promise<void> {
		const { data }: { data: ServerDetails[] } = await api.getServers();
		commit("setServersList", data);
	},

	async getFilteredServersPaginated(
		{ commit, state },
		{ params, cancelTokenSource }: CancellableQuery<PaginatedSearchQueryParams>
	): Promise<void> {
		const { data }: { data: ServerDetails[] } = await api.getServers(params, cancelTokenSource);
		commit("addServersNotAlreadyInStore", data);

		if (params.pageSize > data.length) {
			const recordsRetrievalStatus: DeviceRecordsRetrievalStatus = { ...state.recordsRetrievalStatus };

			if (params.searchTerm) {
				recordsRetrievalStatus.allFilteredServersRetrieved = true;
			} else {
				recordsRetrievalStatus.allServersRetrieved = true;
			}
			commit("updateRecordsRetrievalStatus", recordsRetrievalStatus);
		}
	},

	async getServersWithEnabledDeviceTypes({ commit }, params: ServersWithEnabledDeviceTypesParams): Promise<void> {
		const { data }: { data: ServerDetails[] } = await api.getServersWithEnabledDeviceTypes(params);
		commit("addServersNotAlreadyInStore", data);
	},

	async getServer({ commit }, serverId: number): Promise<void> {
		const { data }: { data: ServerDetails } = await api.getServer(serverId);
		commit("addServersNotAlreadyInStore", [data]);
	},

	async getDeviceTypeCounts({ commit }, serverId: number | null): Promise<void> {
		const { data }: { data: DeviceTypeCounts } = await api.getDeviceTypeCounts(serverId);

		if (serverId) {
			commit("addDeviceTypeCountsToCurrentServer", data);
			return;
		}

		commit("setDeviceTypeCounts", data);
	},

	async fetchServerReConfig({ commit }, serverConfig: ServerConfiguration): Promise<void> {
		commit("setGettingConfig", true);
		commit("setDeviceChangeErrors", null);
		commit("setDeviceConfigErrors", null);

		let data : DeviceChange[] = null;
		try {
			data = await devicesApi.reGetConfig(
				serverConfig
			);
		} catch (err) {
			commit("setGettingConfig", false);
			commit("serverDeviceChanges", null);
		}

		if (data == null) {
			return;
		}

		var camelizedData = objectToCamelCase(data);

		commit("setServerDeviceChanges", camelizedData);
		commit("setGettingConfig", false);
	},

	async fetchServerConfig({ commit }, { server, serverType, auth }: FetchConfigForServerParams): Promise<void> {
		commit("setDeviceChangeErrors", null);
		commit("setDeviceConfigErrors", null);

		commit("setGettingConfig", true);
		let data = null;
		try {
			data  = await devicesApi.getConfig(
				serverType.dll,
				serverType.dllDeviceType,
				serverType.urlTemplate,
				"",
				server.host,
				server.port,
				server.username,
				server.password,
				server.extraValue,
				"",
				"",
				"",
				"",
				"json",
				auth
			);
		} catch (err) {
			commit("setServerConfig", null);
			commit("setGettingConfig", false);
		}

		if (data == null) {
			return;
		}

		var camelizedData = objectToCamelCase(data);

		if (camelizedData != null && camelizedData.length > 0 && camelizedData[0] != null) {
			// Sort arrays by name or title
			camelizedData[0].cameras =
				camelizedData[0].cameras != null && camelizedData[0].cameras.length > 0
					? camelizedData[0].cameras.sort((a, b) => (!a.name ? 1 : !b.name ? -1 : a.name.localeCompare(b.name)))
					: [];
			camelizedData[0].alarms =
				camelizedData[0].alarms != null && camelizedData[0].alarms.length > 0
					? camelizedData[0].alarms.sort((a, b) => (!a.title ? 1 : !b.title ? -1 : a.title.localeCompare(b.title)))
					: [];
			camelizedData[0].relays =
				camelizedData[0].relays != null && camelizedData[0].relays.length > 0
					? camelizedData[0].relays.sort((a, b) => (!a.name ? 1 : !b.name ? -1 : a.name.localeCompare(b.name)))
					: [];
			camelizedData[0].audios =
				camelizedData[0].audios != null && camelizedData[0].audios.length > 0
					? camelizedData[0].audios.sort((a, b) => (!a.name ? 1 : !b.name ? -1 : a.name.localeCompare(b.name)))
					: [];
		}

		commit("setServerConfig", camelizedData);
		commit("setGettingConfig", false);
	},

	async fetchServersByGroupId({ commit }, { groupId, pageSize }: FetchServerByGroupParams): Promise<void> {
		const { data } = await api.fetchServers(groupId, pageSize);
		commit("setServersList", data);
	},

	async searchServersByGroupId({ commit }, { groupId, pageSize, filter }: SearchServerByGroupParams): Promise<void> {
		const { data } = await api.fetchServers(groupId, pageSize, filter);
		commit("setFilteredServersList", data);
	},

	async addServer({ commit }, newServer: ServerDetails): Promise<void> {
		const { data }: { data: number } = await api.addServer(newServer);

		newServer.serverID = data;

		const newServerForStore: ServerDetails = { ...newServer };
		commit("addServer", newServerForStore);
		commit("setCurrentServer", newServerForStore);
		commit("setNotificationOptions", {
			type: "success",
			title: "Device Created",
			text: `"${newServer.title}" was created.`
		});
	},

	async updateServer({ commit, state }, updatedServer: ServerDetails): Promise<void> {
		await api.updateServer(updatedServer);

		const serverForStore = { ...updatedServer, deviceTypeCounts: state.currentServer.deviceTypeCounts };
		commit("setCurrentServer", serverForStore);
		commit("updateServer", serverForStore);

		commit("setNotificationOptions", {
			type: "success",
			title: "Device Updated",
			text: `"${updatedServer.title}" was updated.`
		});
	},

	async deleteServer({ commit }, serverId: number): Promise<void> {
		await api.deleteServer(serverId);

		commit("deleteServer", serverId);
		commit("setNotificationOptions", { type: "success", title: "Device Deleted" });
	},

	async refreshDevicesFromChanges({ commit }, deviceChanges: DeviceChange[]): Promise<void> {
		// devices updated
		let updatedDevices = deviceChanges.filter(function (obj){
			return obj.updateType === UpdateType.Update;
		}).map(u => u.updatedOrAddedDevice);

		if (updatedDevices != null && updatedDevices.length > 0) {
			commit("updateDevices", updatedDevices);
		}

		// devices removed
		let deletedDevices = deviceChanges.filter(function (obj){
			return obj.updateType === UpdateType.Delete;
		}).map(u => u.deviceId);

		if (deletedDevices != null && deletedDevices.length > 0) {
			commit("deleteDevices", deletedDevices);
		}
	},

	async applyDeviceChangesForServer({ commit, state, dispatch }, changes: DeviceChanges): Promise<void> {
		let deviceChangesResponse : DeviceChangesResponse = null;

		try {
			deviceChangesResponse = await devicesApi.applyChanges(changes);
		}
		catch (err) {
			commit("setSettingConfig", false);
		}

		if (deviceChangesResponse == null) {
			return;
		}

		let setServerModalVisible = false;

		if (deviceChangesResponse.successfulChanges != null) {

			if (deviceChangesResponse.successfulChanges.cameraChanges != null && deviceChangesResponse.successfulChanges.cameraChanges.length > 0) {
				await dispatch("refreshDevicesFromChanges", deviceChangesResponse.successfulChanges.cameraChanges);
			}

			if (deviceChangesResponse.successfulChanges.audioChanges != null && deviceChangesResponse.successfulChanges.audioChanges.length > 0) {
				await dispatch("refreshDevicesFromChanges", deviceChangesResponse.successfulChanges.audioChanges);
			}

			if (deviceChangesResponse.successfulChanges.doorChanges != null && deviceChangesResponse.successfulChanges.doorChanges.length > 0) {
				await dispatch("refreshDevicesFromChanges", deviceChangesResponse.successfulChanges.doorChanges);
			}

			if (deviceChangesResponse.successfulChanges.outputChanges != null && deviceChangesResponse.successfulChanges.outputChanges.length > 0) {
				await dispatch("refreshDevicesFromChanges", deviceChangesResponse.successfulChanges.outputChanges);
			}

			if (deviceChangesResponse.successfulChanges.alarmChanges != null && deviceChangesResponse.successfulChanges.alarmChanges.length > 0) {
				await dispatch("responses/refreshResponsesFromChanges", deviceChangesResponse.successfulChanges.alarmChanges);
			}
		}

		if (deviceChangesResponse.errors != null) {
			if ((deviceChangesResponse.errors.alarmChanges != null && deviceChangesResponse.errors.alarmChanges.length > 0) ||
				(deviceChangesResponse.errors.audioChanges != null && deviceChangesResponse.errors.audioChanges.length > 0) ||
				(deviceChangesResponse.errors.cameraChanges != null && deviceChangesResponse.errors.cameraChanges.length > 0) ||
				(deviceChangesResponse.errors.outputChanges != null && deviceChangesResponse.errors.outputChanges.length > 0) ||
				(deviceChangesResponse.errors.doorChanges != null && deviceChangesResponse.errors.doorChanges.length > 0)) {
					setServerModalVisible = true;
				}
		}

		commit("setDeviceChangeErrors", deviceChangesResponse.errors);
		commit("setServerModalVisible", setServerModalVisible);

		commit("setNotificationOptions", {
			type: "success",
			title: "Re-Get Config",
			text: `Changes Saved.`
		});
	},

	async addDevicesForServer({ commit, state }, serverConfig: ServerConfiguration): Promise<void> {
		let serverConfigResponse : ServerConfigurationResponse = null;

		try {
			serverConfigResponse = await devicesApi.createConfig(serverConfig);
		} catch (err) {
			commit("setSettingConfig", false);
		}

		if (serverConfigResponse == null) {
			return;
		}

		let serverConfigs = [];
		serverConfigs.push(serverConfig.deviceConfiguration);

		commit("setServerConfig", serverConfigs);

		const deviceTypeCounts: DeviceTypeCounts = { ...state.deviceTypeCounts };
		const deviceTypeCountsForCurrentServer: DeviceTypeCounts = state.currentServer && {
			...state.currentServer.deviceTypeCounts
		};

		let alarmCounts: number = state.alarms;
		let alarmCountsForCurrentServer: number = state.currentServer!= null ? state.currentServer.alarms : 0;

		if (serverConfigResponse.successfulUpdates != null) {
			// set device counts by taking away current device counts for server and then adding new device counts for server
			deviceTypeCounts.audios = (deviceTypeCounts.audios - deviceTypeCountsForCurrentServer.audios) + (serverConfigResponse.successfulUpdates.audios != null ? serverConfigResponse.successfulUpdates.audios.length : 0);
			deviceTypeCounts.cameras = (deviceTypeCounts.cameras - deviceTypeCountsForCurrentServer.cameras) + (serverConfigResponse.successfulUpdates.cameras != null ? serverConfigResponse.successfulUpdates.cameras.length : 0);
			deviceTypeCounts.doors = (deviceTypeCounts.doors - deviceTypeCountsForCurrentServer.doors) + (serverConfigResponse.successfulUpdates.doors != null ? serverConfigResponse.successfulUpdates.doors.length : 0);
			deviceTypeCounts.outputs = (deviceTypeCounts.outputs - deviceTypeCountsForCurrentServer.outputs) + (serverConfigResponse.successfulUpdates.outputs != null ? serverConfigResponse.successfulUpdates.outputs.length : 0);
			alarmCounts = (alarmCounts - alarmCountsForCurrentServer) + (serverConfigResponse.successfulUpdates.alarms != null ? serverConfigResponse.successfulUpdates.alarms.length : 0);

			// set new device counts for server
			deviceTypeCountsForCurrentServer.audios = serverConfigResponse.successfulUpdates.audios != null ? serverConfigResponse.successfulUpdates.audios.length : 0;
			deviceTypeCountsForCurrentServer.cameras = serverConfigResponse.successfulUpdates.cameras != null ? serverConfigResponse.successfulUpdates.cameras.length : 0;
			deviceTypeCountsForCurrentServer.doors = serverConfigResponse.successfulUpdates.doors != null ? serverConfigResponse.successfulUpdates.doors.length : 0;
			deviceTypeCountsForCurrentServer.outputs = serverConfigResponse.successfulUpdates.outputs != null ? serverConfigResponse.successfulUpdates.outputs.length : 0;
			alarmCountsForCurrentServer = serverConfigResponse.successfulUpdates.alarms != null ? serverConfigResponse.successfulUpdates.alarms.length : 0;
		}

		commit("setDeviceTypeCounts", deviceTypeCounts);
		commit("setAlarmCounts", alarmCounts);

		if (state.currentServer) {
			commit("addDeviceTypeCountsToCurrentServer", deviceTypeCountsForCurrentServer);
			commit("addAlarmCountsToCurrentServer", alarmCountsForCurrentServer);
		}

		commit("setNotificationOptions", {
			type: "success",
			title: "Device Config Created",
			text: `Config was created for "${serverConfig.deviceConfiguration.name}".`
		});
		commit("setSettingConfig", false);

		let setServerModalVisible = false;

		if (serverConfigResponse.errors != null) {
			if ((serverConfigResponse.errors.alarms != null && serverConfigResponse.errors.alarms.length > 0) ||
				(serverConfigResponse.errors.audios != null && serverConfigResponse.errors.audios.length > 0) ||
				(serverConfigResponse.errors.cameras != null && serverConfigResponse.errors.cameras.length > 0) ||
				(serverConfigResponse.errors.outputs != null && serverConfigResponse.errors.outputs.length > 0) ||
				(serverConfigResponse.errors.doors != null && serverConfigResponse.errors.doors.length > 0)) {
					setServerModalVisible = true;
				}
		}

		commit("setDeviceConfigErrors", serverConfigResponse.errors);
		commit("setServerModalVisible", setServerModalVisible);
	},

	async addDevice({ commit, state }, newDevice: IDeviceDetails): Promise<void> {
		const { data }: { data: DeviceDetails } = await api.addDevice(newDevice);
		commit("addDevice", data);

		const deviceTypeCounts: DeviceTypeCounts = { ...state.deviceTypeCounts };
		const deviceTypeCountsForCurrentServer: DeviceTypeCounts = state.currentServer && {
			...state.currentServer.deviceTypeCounts
		};

		const notificationTitle = `${DeviceTypeIdentifier[state.currentDeviceTypeIdentifier]} Created`;

		switch (state.currentDeviceTypeIdentifier) {
			case DeviceTypeIdentifier.Camera: {
				deviceTypeCounts.cameras++;

				if (state.currentServer) {
					deviceTypeCountsForCurrentServer.cameras++;
				}

				break;
			}
			case DeviceTypeIdentifier.Audio: {
				deviceTypeCounts.audios++;

				if (state.currentServer) {
					deviceTypeCountsForCurrentServer.audios++;
				}

				break;
			}
			case DeviceTypeIdentifier.Output: {
				deviceTypeCounts.outputs++;

				if (state.currentServer) {
					deviceTypeCountsForCurrentServer.outputs++;
				}

				break;
			}
			case DeviceTypeIdentifier.Door: {
				deviceTypeCounts.doors++;

				if (state.currentServer) {
					deviceTypeCountsForCurrentServer.doors++;
				}

				break;
			}
		}

		commit("setDeviceTypeCounts", deviceTypeCounts);

		if (state.currentServer) {
			commit("addDeviceTypeCountsToCurrentServer", deviceTypeCountsForCurrentServer);
		}

		commit("setNotificationOptions", {
			type: "success",
			title: notificationTitle,
			text: `"${newDevice.title}" was created.`
		});
	},

	async updateDevice({ commit, state }, updatedDevice: DeviceDetails) {
		const { data }: { data: DeviceDetails } = await api.updateDevice(updatedDevice);

		if (state.currentServer && state.currentServer.serverID !== updatedDevice.serverID) {
			const deviceTypeCountsForCurrentServer: DeviceTypeCounts = {
				...state.currentServer.deviceTypeCounts
			};

			switch (state.currentDeviceTypeIdentifier) {
				case DeviceTypeIdentifier.Camera: {
					deviceTypeCountsForCurrentServer.cameras--;
					break;
				}
				case DeviceTypeIdentifier.Audio: {
					deviceTypeCountsForCurrentServer.audios--;
					break;
				}
				case DeviceTypeIdentifier.Output: {
					deviceTypeCountsForCurrentServer.outputs--;
					break;
				}
				case DeviceTypeIdentifier.Door: {
					deviceTypeCountsForCurrentServer.doors--;
					break;
				}
			}

			commit("addDeviceTypeCountsToCurrentServer", deviceTypeCountsForCurrentServer);
		}

		commit("updateDevice", data);

		const notificationTitle = `${DeviceTypeIdentifier[state.currentDeviceTypeIdentifier]} Updated`;

		commit("setNotificationOptions", {
			type: "success",
			title: notificationTitle,
			text: `"${updatedDevice.title}" was updated.`
		});
	},

	async deleteDevice({ commit, state }, device: DeviceDetails): Promise<void> {
		await api.deleteDevice(device.deviceID);
		commit("deleteDevice", device.deviceID);

		const deviceTypeCounts: DeviceTypeCounts = { ...state.deviceTypeCounts };
		const deviceTypeCountsForCurrentServer: DeviceTypeCounts = state.currentServer && {
			...state.currentServer.deviceTypeCounts
		};

		const notificationTitle = `${DeviceTypeIdentifier[state.currentDeviceTypeIdentifier]} Deleted`;

		switch (state.currentDeviceTypeIdentifier) {
			case DeviceTypeIdentifier.Camera: {
				deviceTypeCounts.cameras--;

				if (state.currentServer) {
					deviceTypeCountsForCurrentServer.cameras--;
				}

				break;
			}
			case DeviceTypeIdentifier.Audio: {
				deviceTypeCounts.audios--;

				if (state.currentServer) {
					deviceTypeCountsForCurrentServer.audios--;
				}

				break;
			}
			case DeviceTypeIdentifier.Output: {
				deviceTypeCounts.outputs--;

				if (state.currentServer) {
					deviceTypeCountsForCurrentServer.outputs--;
				}

				break;
			}
			case DeviceTypeIdentifier.Door: {
				deviceTypeCounts.doors--;

				if (state.currentServer) {
					deviceTypeCountsForCurrentServer.doors--;
				}

				break;
			}
		}

		commit("setDeviceTypeCounts", deviceTypeCounts);

		if (state.currentServer) {
			commit("addDeviceTypeCountsToCurrentServer", deviceTypeCountsForCurrentServer);
		}

		commit("setNotificationOptions", { type: "success", title: notificationTitle });
	},
	async getDeviceTypes({ commit }): Promise<void> {
		const deviceTypes: ServerType[] = await api.getDeviceTypes();
		commit("setServerTypes", deviceTypes);
	},
	async getFilteredDeviceTypesPaginated(
		{ commit, state },
		{ params, cancelTokenSource }: CancellableQuery<PaginatedSearchQueryParams>
	): Promise<void> {
		const deviceTypes: ServerType[] = await api.getDeviceTypes(params, cancelTokenSource);
		commit("addServerTypesNotAlreadyInStore", deviceTypes);
	},
	async updateServerType({ commit }, updatedServerType: ServerType): Promise<void> {
		await api.updateServerType(updatedServerType);
		commit("setServerType", updatedServerType);
	},
	async fetchDevicesForAllGroupsByType(
		{ commit, state },
		{ params, cancelTokenSource }: CancellableQuery<GetDevicesByTypeParams>
	): Promise<void> {
		const { data }: { data: IDeviceDetails[] } = await api.getDevicesForAllGroupsByType(params, cancelTokenSource);
		commit("addDevicesNotAlreadyInStore", data);

		if (params.pageSize > data.length) {
			let recordsRetrievalStatus: DeviceRecordsRetrievalStatus;

			if (params.serverId) {
				recordsRetrievalStatus = { ...state.currentServer.recordsRetrievalStatus };
			} else {
				recordsRetrievalStatus = { ...state.recordsRetrievalStatus };
			}

			switch (params.deviceTypeId) {
				case DeviceType.Camera: {
					if (params.searchTerm) {
						recordsRetrievalStatus.allFilteredCamerasRetrieved = true;
					} else {
						recordsRetrievalStatus.allCamerasRetrieved = true;
					}

					break;
				}
				case DeviceType.Audio: {
					if (params.searchTerm) {
						recordsRetrievalStatus.allFilteredAudiosRetrieved = true;
					} else {
						recordsRetrievalStatus.allAudiosRetrieved = true;
					}

					break;
				}
				case DeviceType.Relay: {
					if (params.selectOutputs) {
						if (params.searchTerm) {
							recordsRetrievalStatus.allFilteredOutputsRetrieved = true;
						} else {
							recordsRetrievalStatus.allOutputsRetrieved = true;
						}
					} else if (params.searchTerm) {
						recordsRetrievalStatus.allFilteredDoorsRetrieved = true;
					} else {
						recordsRetrievalStatus.allDoorsRetrieved = true;
					}

					break;
				}
			}

			if (params.serverId) {
				commit("updateCurrentServerRecordsRetrievalStatus", recordsRetrievalStatus);
				return;
			}

			commit("updateRecordsRetrievalStatus", recordsRetrievalStatus);
		}
	},

	async fetchDevicesByType(
		{ commit },
		{ groupId, deviceTypeId, filter }: GetDeviceForGroupByTypeParams
	): Promise<void> {
		const { data }: { data: IDeviceDetails[] } = await api.getDevicesByType(groupId, deviceTypeId, filter);
		commit("setDevices", data);
	},

	async fetchDevicesByTypeAndAppend(
		{ commit },
		{ groupId, deviceTypeId, filter }: GetDeviceForGroupByTypeParams
	): Promise<void> {
		const { data }: { data: IDeviceDetails[] } = await api.getDevicesByType(groupId, deviceTypeId, filter);
		commit("appendDevices", data);
	},

	async fetchDevicesByTypeAndAppendNoFilter(
		{ commit }, { groupId, params }
	): Promise<void> {
		const { data }: { data: IDeviceDetails[] } = await api.devicesByGroup(groupId, params);
		commit("appendDevices", data);
	},

	async fetchDeviceById(
		{ commit },
		{ deviceId, params }: { deviceId: number; params: GetDeviceParams }
	): Promise<void> {
		const { data } = await api.loadDeviceById(deviceId, params);
		commit("setDevice", data);
	},

	async getServerLineProfiles({ commit }): Promise<void> {
		const { data } = await api.fetchServerLineProfiles();
		commit("setServerLineProfiles", data);
	},

	async getDevicesByTitle({ commit }, deviceTitles: string[]): Promise<void> {
		const { data } = await api.getDevicesByTitle(deviceTitles);
		commit("setDevices", data);
	},

	async getCameraPresets({ commit }, device: IDeviceDetails): Promise<DeviceConfigurationPreset[]> {
		if (device.deviceTypeID !== DeviceType.Camera) {
			return [];
		}

		const presets = await api.getCameraPresets(device);
		return presets;
	},

	async createNewCameraPreset({ commit }, presetConfigPayload : DeviceConfigurationPresetPayload): Promise<boolean> {
		var result = await api.createNewCameraPreset(presetConfigPayload);

		return result === true;
	},

	async updateCameraPreset({ commit }, presetConfigPayload : DeviceConfigurationPresetPayload): Promise<boolean> {
		var result = await api.updateCameraPreset(presetConfigPayload);

		return result === true;
	},

	async deleteCameraPreset({ commit }, presetConfigPayload : DeviceConfigurationPresetPayload): Promise<boolean> {
		var result = await api.deleteCameraPreset(presetConfigPayload);

		return result === true;
	}
};
